import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

import * as dayjs from 'dayjs';
import * as dayjsLocaleDe from 'dayjs/locale/de';
import * as dayjsLocalePl from 'dayjs/locale/pl';
import * as dayjsLocaleDa from 'dayjs/locale/da';
import * as dayjsLocaleES from 'dayjs/locale/es';

import * as LANGUAGE_DATA from './../Country_config.json';
import { IAppConfig } from './config/app-config.model';
import { APP_CONFIG } from './config/app-config.service';

const CURRENT_LANGUAGE_STORAGE_KEY = 'current_language';

export interface Language {
  ID: number;
  Name: string;
  Culture_Code: string;
  UI_Culture_Code: string;
  enable?: boolean
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  constructor(
    public translate: TranslateService,
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private route: ActivatedRoute,
  ) { 
    this.loadLanguages();

    this.checkCurrentLanguageFromUrl();
  }

  languages: Language[] = [];
  currentLanguageCode = 'en'
  locales = [dayjsLocaleDa, dayjsLocaleDe, dayjsLocalePl, dayjsLocaleES];

  loadLanguages(){
    const availableLanguages = ((LANGUAGE_DATA as any).default as Language[]).filter(e => e.enable);
    this.languages = [...availableLanguages];

    if(this.config.availableLanguageCodes && this.config.availableLanguageCodes.length){
      const languages = availableLanguages.filter(e => this.config.availableLanguageCodes?.includes(e.UI_Culture_Code));
      if(languages.length){
        this.languages = [...languages];
      }
    }
  }

  checkCurrentLanguageFromUrl(){
    const url = window.location.href;
    const reg = /lang=([a-z]{2})/i;
    if(!reg.test(url)) return;

    const match = reg.exec(url);
    if(!match) return;
    const lang = match[1].toLowerCase();

    if(lang && this.isLanguageAvailable(lang)){
      localStorage.setItem(CURRENT_LANGUAGE_STORAGE_KEY, lang);
    }
  }

  init(){
    
    this.translate.addLangs(this.languages.map((e) => e.UI_Culture_Code));

    const currentLanguage = localStorage.getItem(CURRENT_LANGUAGE_STORAGE_KEY);
    this.translate.setDefaultLang('en');
    if(currentLanguage && this.isLanguageAvailable(currentLanguage)){
      dayjs.locale(currentLanguage);
      this.translate.use(currentLanguage);
      this.currentLanguageCode = currentLanguage;
    }
  }

  saveToLocal(lang: string){
    localStorage.setItem(CURRENT_LANGUAGE_STORAGE_KEY, lang)
  }

  setLanguage(lang: string, disableReloadPage = false){
    if(!this.isLanguageAvailable(lang)){
      return;
    }
    this.currentLanguageCode = lang;
    this.saveToLocal(lang);

    const currentURL = window.location.href;
    const langReg = /lang=([a-z]{2})/i;
    if(langReg.test(currentURL)){
      const match = langReg.exec(currentURL);
      if(match && match[1].toLowerCase() !== lang.toLowerCase()){
        let url = currentURL.replace(/lang=[a-z]{2}/i, '');
        window.location.href = url;
        return;
      }
    }

    if(!disableReloadPage && this.shouldReloadAfterLanguageChanged()){
      window.location.reload();
    }
    else{
      dayjs.locale(lang);
      this.translate.use(lang);
    }
  }

  shouldReloadAfterLanguageChanged(){
    if(/(payment|action-list)/i.test(window.location.href)){
      return true;
    }
    return false;
  }

  getAvailableLanguages(): Language[]{
    return this.languages;
  }

  getCurrentLanguageCode(): string{
    return this.currentLanguageCode;
  }

  getCurrentLanguage(): Language | undefined{
    return this.languages.find(e => e.UI_Culture_Code === this.currentLanguageCode);
  }

  getCurrentCultureCode(){
    const language = this.languages.find(e => e.UI_Culture_Code === this.currentLanguageCode);
    return language ? language.Culture_Code : 'en-US';
  }

  isLanguageAvailable(code: string){
    if(!code) return false;
    code = code.toLowerCase();
    const lang = this.languages.find(e => e.UI_Culture_Code.toLowerCase() === code);
    return lang ? true: false;
  }
}
